import cx from 'classnames';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import PageFooter from '@/common/PageFooter';
import PageHeader from '@/common/PageHeader';
import Breadcrumbs from '@/vdp/BreadCrumbs';
import dynamicLoader from '@/vdp/dynamicLoader';
import { MobileGallery } from '@/vdp/Gallery';
import Hero from '@/vdp/Hero';
import LowerSections from '@/vdp/VehicleDetailsContainer/LowerSections';
import ShowConditionally from '@/vdp/VehicleDetailsContainer/ShowConditionally';
import StickyBannerObserver from '@/vdp/VehicleDetailsContainer/StickyBannerObserver';
import VehicleInfoCard from '@/vdp/VehicleInfoCard';
import { isTest } from '~/constants/environment';
import Section, { SectionId } from '~/constants/vdp/section';
import { useIsAppRouter } from '~/context/common/StaticContext';
import { useIsNewVehicle } from '~/context/vdp/VehicleContext';
import useIsNotAvailableForImmediatePurchase from '~/hooks/vdp/selectors/useIsNotAvailableForImmediatePurchase';
import NativeError from '~/native/NativeError';
import {
  GridLayout,
  MainContentWrapper,
  sectionBottomPadding,
  sectionXPadding,
  sectionYPadding
} from './styles';
import { ContentWrapper, Wrapper } from './VehicleDetailsContainer.styles';

const AboutThePrice = dynamic(
  () => import(/* webpackChunkName: "AboutThePrice" */ '@/vdp/AboutThePrice'),
  { ssr: false }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  { ssr: false, loading: dynamicLoader(Section.Disclaimer) }
);

const FeatureBranch = dynamic(
  () =>
    import(/* webpackChunkName: "FeatureBranch" */ '@/common/FeatureBranch'),
  { ssr: false }
);

const FeaturesAndHighlights = dynamic(
  () =>
    import(
      /* webpackChunkName: "FeaturesAndHighlights" */ '@/vdp/FeaturesAndHighlights'
    ),
  { ssr: false }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const MobileCta = dynamic(
  () => import(/* webpackChunkName: "MobileCta" */ '@/vdp/MobileCta'),
  { ssr: false }
);

const NewCarBanner = dynamic(
  () => import(/* webpackChunkName: "NewCarBanner" */ '@/vdp/NewCarBanner'),
  {
    ssr: false
  }
);

const PickupAndDelivery = dynamic(
  () =>
    import(
      /* webpackChunkName: "PickupAndDelivery" */ '@/vdp/PickupAndDelivery'
    ),
  { ssr: false }
);

const PriceDetails = dynamic(
  () => import(/* webpackChunkName: "PriceDetails" */ '@/vdp/PriceDetails'),
  { ssr: false }
);

const RecommendedVehicles = dynamic(
  () =>
    import(
      /* webpackChunkName: "RecommendedVehicles" */ '@/vdp/RecommendedVehicles'
    ),
  { ssr: false }
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const SubscribedToNotificationsBanner = dynamic(
  () =>
    import(
      /* webpackChunkName: "SubscribedToNotificationsBanner" */ '@/vdp/SubscribedToNotificationsBanner'
    ),
  { ssr: false }
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  { ssr: false }
);

export default function VehicleDetailsLiteContainer() {
  const isAppRouter = useIsAppRouter();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const isNewVehicle = useIsNewVehicle();
  const isNotAvailableForImmediatePurchase =
    useIsNotAvailableForImmediatePurchase();

  return (
    <>
      {!isAppRouter && <PageHeader />}
      {isTest && !isAppRouter && <FeatureBranch />}
      <NativeError />
      <Wrapper ref={wrapperRef} id={SectionId.Root}>
        <SubscribedToNotificationsBanner />
        <ContentWrapper>
          <Breadcrumbs />
          <GridLayout ref={gridRef}>
            <Hero />
            <VehicleInfoCard />
            <MainContentWrapper>
              <ShowConditionally wip={false} operation="and" mobile>
                <MobileGallery />
              </ShowConditionally>
              <ShowConditionally mobile>
                <StickyBannerObserver />
              </ShowConditionally>
              {isNewVehicle && <NewCarBanner />}
              <ShowConditionally deliveryPickup>
                <div className={cx(sectionBottomPadding, 'pt-4xl sm:pt-0')}>
                  <PickupAndDelivery />
                </div>
              </ShowConditionally>
              <div>
                <ShowConditionally
                  aboutPrice
                  notAvailableForImmediatePurchase={false}
                >
                  <AboutThePrice gridRef={gridRef} wrapperRef={wrapperRef} />
                </ShowConditionally>
                <div
                  className={cx(sectionXPadding, sectionYPadding, 'first:pt-0')}
                >
                  <RecommendedVehicles
                    isInGridLayout
                    locationOnPage={
                      isNotAvailableForImmediatePurchase ? 'top' : 'default'
                    }
                  />
                </div>
                <ShowConditionally aboutPrice notAvailableForImmediatePurchase>
                  <AboutThePrice gridRef={gridRef} wrapperRef={wrapperRef} />
                </ShowConditionally>
              </div>
              <PriceDetails gridRef={gridRef} wrapperRef={wrapperRef} />
              <FeaturesAndHighlights
                gridRef={gridRef}
                wrapperRef={wrapperRef}
              />
              <VehicleDetails />
              <LowerSections
                gridRef={gridRef}
                showInitialDivider
                wrapperRef={wrapperRef}
              />
            </MainContentWrapper>
          </GridLayout>
        </ContentWrapper>
        <Disclaimer />
        <ShowConditionally mobile>
          <StickyBanner />
        </ShowConditionally>
        <ShowConditionally mobile renderStickyCta>
          <MobileCta />
        </ShowConditionally>
      </Wrapper>

      {!isAppRouter && <PageFooter className="bg-canvas-muted" />}
      <Modals />
    </>
  );
}
